import { h, createApp } from "vue";
import { ElTooltip } from "element-plus";
import { parseJSONWithStringNumbers } from "@/flow/application/jsonParseForNumbers";
import {
  normalizeMetaoutputReference,
  normalizeOutputReference,
} from "@/flow/utils/outputReferenceUtils";
import {
  resolveOutputPath,
  stringifyOutputPathValue,
} from "@/flow/utils/outputPathResolver";
import { labelConvert } from "@/core/util/variable-label-convert";

export function getType(type: string): string {
  switch (type) {
    case "output":
      return "output-tag";
    case "expressionType":
      return "exp-function-tag";
    case "translationType":
      return "translation-function-tag";
    case "translationOperationType":
      return "opr-function-tag";
    case "arithmetic-operator":
    case "logical-operator":
    case "relational-operator":
      return "operator-tag";
    case "text-function":
    case "math-function":
    case "general-function":
      return "function-tag";
    case "user-defined-variable":
      return "user-defined-variable-tag";
    case "metaoutput":
      return "metaoutput-tag";
    case "system-variable":
      return "system-variable-tag";
    default:
      return "function-tag";
  }
}

export function createHTMLSpanElement(
  item: any,
  hasChildFlag: boolean,
  idCount: number,
  tooltip?: string | boolean
): HTMLElement {
  const tooltipContent = item.tooltip || "";
  const origId =
    item?.origId !== undefined && item?.origId !== null
      ? String(item.origId)
      : "";

  const isOperator = getType(item?.type) === "operator-tag";
  const displayText =
    hasChildFlag && !isOperator ? `${item?.name}(` : item?.name;

  const app = createApp({
    render() {
      if (item?.type?.includes("-opr")) {
        return h("span", {
          name: item.name,
          origId: origId || undefined,
          type: item.type,
          id: `element-${idCount}`,
          function: item.function ?? "",
          class: ["tag", getType(item.type)],
          contentEditable: "false",
          innerText: displayText || "",
        });
      }

      const tooltipClass =
        tooltipContent.length > 200
          ? "scrollable-injected-tooltip-content"
          : "";

      return h(
        ElTooltip,
        {
          placement: "top",
          popperClass: "is-injected",
        },
        {
          default: () =>
            h("span", {
              name: item.name,
              origId: origId || undefined,
              type: item.type,
              id: `element-${idCount}`,
              function: item.function ?? "",
              class: ["tag", getType(item.type)],
              contentEditable: "false",
              innerText: displayText || "",
              "data-tooltip": tooltipContent,
            }),
          content: () => h("div", { class: tooltipClass }, tooltipContent),
        }
      );
    },
  });

  const container = document.createElement("div");
  app.mount(container);

  const element = container.firstElementChild as HTMLElement;
  setTagDisplayText(element, displayText || "");
  return element;
}

/**
 * Splits a tag's display text into segments at meaningful boundaries so that a
 * <wbr> (line-break opportunity) can be inserted between them. Break points are
 * placed after separator groups (`.`, `/`, `_`, `-`, `:`), around array
 * indexes (`[`/`]`), at camelCase transitions, and before the final word in
 * acronym-to-word transitions such as `HTTPResponse`.
 *
 * Invariant: `splitForWrap(text).join("") === text`. No characters are added or
 * removed, so the underlying expression value is never altered.
 */
export function splitForWrap(text: string): string[] {
  if (!text) return [];

  const isSeparator = (c: string): boolean =>
    c === "." || c === "/" || c === "_" || c === "-" || c === ":";
  const isLower = (c: string): boolean => c >= "a" && c <= "z";
  const isUpper = (c: string): boolean => c >= "A" && c <= "Z";
  const isDigit = (c: string): boolean => c >= "0" && c <= "9";

  const segments: string[] = [];
  let current = "";

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const prev = i > 0 ? text[i - 1] : "";
    const next = i + 1 < text.length ? text[i + 1] : "";

    // Break BEFORE an array index, camelCase word, or acronym's final word.
    const breakBeforeBracket = ch === "[";
    const camelCaseBoundary = isUpper(ch) && (isLower(prev) || isDigit(prev));
    const acronymBoundary = isUpper(prev) && isUpper(ch) && isLower(next);

    if (
      current.length > 0 &&
      (breakBeforeBracket || camelCaseBoundary || acronymBoundary)
    ) {
      segments.push(current);
      current = "";
    }

    current += ch;

    // Keep adjacent separators together (for example `./`), then break.
    const endsSeparatorGroup = isSeparator(ch) && !isSeparator(next);
    if (endsSeparatorGroup || ch === "]") {
      segments.push(current);
      current = "";
    }
  }

  if (current.length > 0) segments.push(current);

  return segments;
}

export function getTagDisplaySegments(text: string): string[] {
  return splitForWrap(text);
}

/**
 * Sets a tag span's visible text while inserting <wbr> break opportunities at
 * meaningful boundaries (see {@link splitForWrap}). Uses text nodes + <wbr>
 * elements so that `textContent` / copy-to-clipboard remain exactly the passed
 * text (a <wbr> contributes nothing to textContent). Also tags the span with
 * the `wrappable-tag` class so scoped CSS can allow wrapping.
 */
export function setTagDisplayText(span: HTMLElement, text: string): void {
  const value = text ?? "";
  const segments = splitForWrap(value);

  span.textContent = "";
  segments.forEach((segment, index) => {
    if (index > 0) {
      span.appendChild(document.createElement("wbr"));
    }
    span.appendChild(document.createTextNode(segment));
  });

  span.classList.add("wrappable-tag");
}

export function getCaretPosition(node: Node): number {
  let position = 0;
  const isSupported = typeof window.getSelection() !== "undefined";
  if (isSupported) {
    const selection = window.getSelection();
    if (selection.rangeCount !== 0) {
      const range = window.getSelection().getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(node);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      position = preCaretRange.toString().length;
    }
  }
  return position;
}

export function setCaretPosition(
  node: Node,
  id: string,
  offset?: number
): void {
  const el = document.getElementById(id);
  const selection = window.getSelection();
  const range = document.createRange();
  selection.removeAllRanges();

  if (offset !== undefined) {
    range.setStart(node, offset);
  } else {
    range.selectNodeContents(el);
    range.setEndAfter(node);
    range.collapse(false);
  }

  selection.addRange(range);
  el.focus();
}
export function saveCaretPosition(node: Node): Range | null {
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    return sel.getRangeAt(0);
  }
  return null;
}
export function restoreCaretPosition(
  container: HTMLElement,
  storedRange: Range | null
): void {
  if (container && storedRange) {
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(storedRange);
      container.focus();
    }
  }
}
export function ensureCaretRestored(
  containerId: string,
  storedRange: Range | null
): void {
  const container = document.getElementById(containerId);
  if (container && !container.contains(document.activeElement)) {
    restoreCaretPosition(container, storedRange);
  }
}
export function hasChild(type: string): boolean {
  const typesWithChild = [
    "text-function",
    "math-function",
    "general-function",
    "date-function",
    "logic-function",
    "encode-decode-function",
    "array-function",
  ];
  return typesWithChild.includes(type);
}
export function handleBackspaceZWSP(
  div: HTMLElement,
  controlDivId: string
): boolean {
  const sel = window.getSelection();
  if (!sel || !sel.isCollapsed || sel.rangeCount === 0) {
    return false;
  }
  const range = sel.getRangeAt(0);
  const { startContainer, startOffset } = range;

  let targetNode: Node | null = null;
  if (startContainer.nodeType === Node.TEXT_NODE) {
    targetNode = startContainer;
  } else if (startOffset > 0) {
    targetNode = startContainer.childNodes[startOffset - 1];
  }

  if (
    targetNode &&
    targetNode.nodeType === Node.TEXT_NODE &&
    /^\u200B+$/.test((targetNode as Text).textContent || "")
  ) {
    const toRemove: Node[] = [];
    let node: Node | null = targetNode;
    while (
      node &&
      node.nodeType === Node.TEXT_NODE &&
      /^\u200B+$/.test((node as Text).textContent || "")
    ) {
      toRemove.push(node);
      node = node.previousSibling;
    }

    if (
      node &&
      node.nodeType === Node.ELEMENT_NODE &&
      (node as HTMLElement).tagName.toLowerCase() === "span"
    ) {
      toRemove.push(node);
    }

    toRemove.forEach((n) => {
      if (n.parentNode === div) {
        div.removeChild(n);
      }
    });

    return true;
    // eslint-disable-next-line
  } else if (
    startContainer.nodeType === Node.TEXT_NODE &&
    (startContainer as Text).data === "\u200B" &&
    startContainer.previousSibling instanceof HTMLElement &&
    startContainer.previousSibling.tagName.toLowerCase() === "span"
  ) {
    const parent = startContainer.parentNode! as HTMLElement;
    const spanNode = startContainer.previousSibling as HTMLElement;

    parent.removeChild(startContainer);

    const zwsp = document.createTextNode("\u200B");
    parent.insertBefore(zwsp, spanNode.nextSibling);

    setCaretPosition(zwsp, controlDivId, 1);

    return true;
  }
  return false;
}
export function isOurSpanHtml(html: string): boolean {
  if (!html) return false;
  return /<span\b(?=[^>]*\bname="[^"]+")(?=[^>]*\btype="[^"]+")(?:[^>]*\borigId="[^"]+")?[^>]*>/i.test(
    html
  );
}

export async function copyContainerContent(containerId: string): Promise<void> {
  const container = document.getElementById(containerId);
  if (!container) throw new Error(`Container not found: ${containerId}`);

  const html = container.innerHTML;
  const text = container.textContent || "";

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  await navigator.clipboard.write([
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    new ClipboardItem({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([text], { type: "text/plain" }),
    }),
  ]);
}

let globalPasteId = 0;

export function handlePastedHtml(
  htmlData: string,
  container: HTMLElement,
  range: Range,
  outputList?: any[],
  metaoutputList?: any[],
  options?: {
    preferOrigId?: boolean;
    allowedTypes?: string[];
  }
): void {
  const parser = new DOMParser();
  const cleanedHtmlData = htmlData
    .replace(/^(?:\r\n|\r|\n)+/, "")
    .replace(/(?:\r\n|\r|\n)+$/, "");
  const doc = parser.parseFromString(cleanedHtmlData, "text/html");
  const fragment = document.createDocumentFragment();

  doc.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const copiedOrigId = el.getAttribute("origId") || "";
      let name = el.getAttribute("name") || "";
      const type = el.getAttribute("type") || "";
      const originalTooltip = el.getAttribute("data-tooltip") || "";
      let referenceOrigId = copiedOrigId;
      let referenceStepNumber: number | undefined;

      if (options?.allowedTypes && !options.allowedTypes.includes(type)) {
        return;
      }

      if (type === "output") {
        const normalizedOutput = normalizeOutputReference({
          name,
          origId: copiedOrigId,
          outputList,
          preferOrigId: options?.preferOrigId,
        });
        name = normalizedOutput.name;
        referenceOrigId = normalizedOutput.origId ?? copiedOrigId;
        referenceStepNumber = normalizedOutput.stepNumber;
      } else if (type === "metaoutput") {
        const normalizedMetaoutput = normalizeMetaoutputReference({
          name,
          origId: copiedOrigId,
          metaoutputList,
          preferOrigId: options?.preferOrigId,
        });
        name = normalizedMetaoutput.name;
        referenceOrigId = normalizedMetaoutput.origId ?? copiedOrigId;
        referenceStepNumber = normalizedMetaoutput.stepNumber;
      }

      if (
        type === "metaoutput" &&
        (!Array.isArray(metaoutputList) || !metaoutputList.length)
      ) {
        return;
      }

      if (!name || !type) {
        return;
      }
      let tooltipToUse = originalTooltip;
      let isValidTag = false;

      if (type === "output" && Array.isArray(outputList)) {
        ({ tooltipToUse, isValidTag } = validateOutputTag(name, outputList));
      }
      if (type === "metaoutput" && Array.isArray(metaoutputList)) {
        ({ tooltipToUse, isValidTag } = validateMetaoutputTag(
          name,
          metaoutputList
        ));
      }
      if ((type === "output" || type === "metaoutput") && !isValidTag) {
        tooltipToUse = "";
      }

      const span = createHTMLSpanElement(
        {
          name,
          type,
          tooltip: tooltipToUse,
          function: el.getAttribute("function"),
          origId: referenceOrigId,
        },
        hasChild(type),
        globalPasteId++,
        tooltipToUse
      );

      const parts =
        type === "output"
          ? name.split("/#")
          : name.split("/#ix-")[1]?.split("@#") || [];
      const idx = parseInt(
        type === "output" ? parts[0].replace("/", "") : parts[0],
        10
      );

      const rawPath = (parts[1] || "").replace(/@#/g, "/");

      const displayNum =
        referenceStepNumber != null ? `${referenceStepNumber}` : `${idx + 1}`;
      setTagDisplayText(span, `${displayNum}./${rawPath}`);
      span.classList.toggle(`${type}-tag`, isValidTag);
      span.classList.toggle(`invalid-${type}-tag`, !isValidTag);

      if (type === "user-defined-variable") {
        const [, key] =
          name.match(/^\/(?:userDefinedVariables\/)?#(.+)$/) || [];
        setTagDisplayText(span, `./${key}`);
        span.classList.add("user-defined-variable-tag");
      }
      if (type === "system-variable") {
        setTagDisplayText(span, labelConvert(name));
        span.classList.add("system-variable-tag");
      }
      if (
        type.includes("-function") ||
        type.includes("-opr") ||
        type.includes("-operator")
      ) {
        setTagDisplayText(span, hasChild(type) ? `${name}(` : name);
      }
      fragment.appendChild(span);
    } else if (node.nodeType === Node.TEXT_NODE) {
      const txt = node.textContent
        ?.replace(/\r\n/g, "\n")
        .replace(/(?:^\n+|\n+$)/g, "")
        .trim();
      if (txt) {
        fragment.appendChild(document.createTextNode(txt));
      }
    } else {
      fragment.appendChild(node.cloneNode(true));
    }
  });

  const inserted = Array.from(fragment.childNodes);

  range.deleteContents();
  range.insertNode(fragment);

  const last = inserted[inserted.length - 1];
  if (last && last.parentNode) {
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      const newRange = document.createRange();
      newRange.setStartAfter(last);
      newRange.collapse(true);
      sel.addRange(newRange);
      container.focus();
    }
  }
}

export function validateOutputTag(name: string, outputList: any[]): any {
  let tooltip = "";
  let valid = false;
  const m = name.match(/^\/(\d+)\/#(.*)$/);
  if (!m) return { tooltipToUse: tooltip, isValidTag: valid };

  const [, idxStr, raw] = m;
  const idx = parseInt(idxStr, 10);

  const key = raw;

  const match = outputList?.find(
    (o) => o.index === idx && typeof o.output === "string"
  );
  if (!match) return { tooltipToUse: tooltip, isValidTag: valid };

  try {
    const parsed = parseJSONWithStringNumbers(match.output);

    if (key === "") {
      tooltip = stringifyOutputPathValue(parsed);
      valid = true;
    } else {
      const resolved = resolveOutputPath(parsed, key);
      tooltip = resolved.found ? stringifyOutputPathValue(resolved.value) : "";
      valid = resolved.found;
    }
  } catch {
    valid = false;
  }

  return { tooltipToUse: tooltip, isValidTag: valid };
}

export function validateMetaoutputTag(
  name: string,
  metaoutputList: any[]
): any {
  let tooltip = "";
  let valid = false;

  const m = name.match(/^\/metaoutput\/#ix-(\d+)(?:@(.*))?$/);
  if (!m) return { tooltipToUse: tooltip, isValidTag: valid };

  const [, idxStr, rawPath = ""] = m;
  const idx = parseInt(idxStr, 10);
  const path = rawPath.replace(/^#/, "");

  const match = metaoutputList?.find(
    (o) => o.index === idx && typeof o.metaoutput === "string"
  );
  if (!match) return { tooltipToUse: tooltip, isValidTag: valid };

  try {
    const parsed = JSON.parse(match.metaoutput);

    if (path === "") {
      tooltip = stringifyOutputPathValue(parsed);
      valid = true;
    } else {
      const resolved = resolveOutputPath(parsed, path);
      tooltip = resolved.found ? stringifyOutputPathValue(resolved.value) : "";
      valid = resolved.found;
    }
  } catch {
    valid = false;
  }

  return { tooltipToUse: tooltip, isValidTag: valid };
}
